/**
 * Pure planting math for the 3D fuel layer (issue #276, phases 2a/2d/3).
 *
 * This is the former FuelLoad3DTrees rebuild() planting loop, ported off THREE
 * and mapbox so it can run in a Web Worker: inputs are flat typed arrays
 * (mercator rings, elevation grid, fire grid) and outputs are per-bucket
 * Float32Arrays of composed 4x4 instance matrices + linear-space colours,
 * transferable back to the main thread with zero copies. No per-instance
 * objects are allocated anywhere (the old InstanceRecord/Matrix4/Color per
 * plant was ~300–500 MB of retained heap and GC storms).
 *
 * Placement is bit-compatible with the old loop: the same deterministic
 * world-space lattice, the same hash channels, the same colour ramps (colour
 * math reproduces THREE.Color's linear-working-space lerp/offsetHSL). New
 * hash channels are only added for the distance taper.
 */

import type {
  Bbox,
  BucketResult,
  ElevGridData,
  FireGridData,
  PlantRequest,
  PlantResult,
  PolySet,
} from "./types";
import { CROWN_FIRE_FROM_GRID, FIRE_NEVER } from "./types";

// ---------------------------------------------------------------------------
// Constants (mirrors FuelLoad3DTrees)
// ---------------------------------------------------------------------------
const GROUND_MIN_ZOOM = 16;
const MIN_CROWN_FRAC = 0.3;
const CROWN_KILL_FRAC = 2 / 3;

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

/** Deterministic [0,1) hash of an integer grid cell — stable across rebuilds. */
function hash2(x: number, z: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(z, 668265263)) | 0;
  h = (h ^ (h >>> 13)) | 0;
  h = Math.imul(h, 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------
// Colour math — reproduces THREE.Color semantics: hex is sRGB, converted to
// the linear working space, and lerp/offsetHSL operate on the linear values.
// ---------------------------------------------------------------------------
const srgbToLinear = (c: number) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

function hexToLinear(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [
    srgbToLinear(((v >> 16) & 255) / 255),
    srgbToLinear(((v >> 8) & 255) / 255),
    srgbToLinear((v & 255) / 255),
  ];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * 6 * (2 / 3 - t);
  return p;
}

class ColorL {
  r = 0;
  g = 0;
  b = 0;
  copy(c: readonly [number, number, number] | ColorL): this {
    if (Array.isArray(c)) {
      this.r = c[0];
      this.g = c[1];
      this.b = c[2];
    } else {
      const o = c as ColorL;
      this.r = o.r;
      this.g = o.g;
      this.b = o.b;
    }
    return this;
  }
  lerp(c: readonly [number, number, number], t: number): this {
    this.r += (c[0] - this.r) * t;
    this.g += (c[1] - this.g) * t;
    this.b += (c[2] - this.b) * t;
    return this;
  }
  offsetHSL(dh: number, ds: number, dl: number): this {
    // getHSL (linear space, THREE-compatible)
    const max = Math.max(this.r, this.g, this.b);
    const min = Math.min(this.r, this.g, this.b);
    let h = 0;
    let s = 0;
    const l = (min + max) / 2;
    if (min !== max) {
      const d = max - min;
      s = l <= 0.5 ? d / (max + min) : d / (2 - max - min);
      switch (max) {
        case this.r:
          h = (this.g - this.b) / d + (this.g < this.b ? 6 : 0);
          break;
        case this.g:
          h = (this.b - this.r) / d + 2;
          break;
        default:
          h = (this.r - this.g) / d + 4;
      }
      h /= 6;
    }
    // setHSL
    h = (((h + dh) % 1) + 1) % 1;
    s = clamp(s + ds, 0, 1);
    const ll = clamp(l + dl, 0, 1);
    if (s === 0) {
      this.r = this.g = this.b = ll;
    } else {
      const q = ll <= 0.5 ? ll * (1 + s) : ll + s - ll * s;
      const p = 2 * ll - q;
      this.r = hue2rgb(p, q, h + 1 / 3);
      this.g = hue2rgb(p, q, h);
      this.b = hue2rgb(p, q, h - 1 / 3);
    }
    return this;
  }
}

// Palette — same hex values the layer used with THREE.Color.
const CONE_LO = hexToLinear("#676b52");
const CONE_HI = hexToLinear("#0a2e17");
const BLOB_LO = hexToLinear("#6e7253");
const BLOB_HI = hexToLinear("#133d1b");
const TRUNK = hexToLinear("#7a6248");
const EUC_LO = hexToLinear("#7a8764");
const EUC_HI = hexToLinear("#62705a");
const EUC_TRUNK = hexToLinear("#908a7d");
const PINE_LO = hexToLinear("#304f36");
const PINE_HI = hexToLinear("#0c3a1f");
const PINE_TRUNK = hexToLinear("#6b4a32");
const SHRUB_LO = hexToLinear("#9aa45c");
const SHRUB_HI = hexToLinear("#5c6536");
const LITTER_LO = hexToLinear("#9c8763");
const LITTER_HI = hexToLinear("#4f3a23");
const LOG_LO = hexToLinear("#33271a");
const LOG_HI = hexToLinear("#574a39");
const NS_SHRUB_LO = hexToLinear("#8a8a4e");
const NS_SHRUB_HI = hexToLinear("#5d6a3a");
const GRASS_LO = hexToLinear("#647236");
const GRASS_HI = hexToLinear("#857946");

// ---------------------------------------------------------------------------
// Geometry helpers over the flat PolySet layout
// ---------------------------------------------------------------------------

/**
 * Row-bucketed edge index for one member polygon at one lattice pitch.
 *
 * The even-odd ray test for a sample point only involves edges whose y-range
 * straddles the point, so bucketing the polygon's edges by lattice row once
 * (O(V + rows)) turns the per-cell test from "every edge of the polygon" into
 * "the few edges crossing this row". Per-row x-extents additionally bound the
 * cells worth visiting at all: a cell left of every edge in its row sees an
 * even crossing count, one right of them sees none — both outside.
 *
 * Results are exactly those of the old full-ring test: the buckets are a
 * conservative superset of the crossing edges and the crossing expression is
 * unchanged. Fuel polygons are raster-derived staircases — thousands of
 * vertices, bounding boxes far larger than their area — so on a large project
 * this is the difference between seconds and tens of milliseconds per rebuild.
 * Buffers are reused across polygons (the worker runs one plant at a time).
 */
class RowEdgeIndex {
  /** CSR row offsets into ei/ej (length rows + 1). */
  rowStart = new Int32Array(1024);
  rowMinX = new Float64Array(1024);
  rowMaxX = new Float64Array(1024);
  private ei = new Int32Array(4096);
  private ej = new Int32Array(4096);
  private cursor = new Int32Array(1024);

  /** Index member polygon `poly` of `set` over lattice rows gz0..gz1 at pitch `s`. */
  build(set: PolySet, poly: number, s: number, gz0: number, gz1: number): this {
    const rows = gz1 - gz0 + 1;
    if (this.rowStart.length < rows + 1) {
      const cap = Math.max(rows + 1, this.rowStart.length * 2);
      this.rowStart = new Int32Array(cap);
      this.rowMinX = new Float64Array(cap);
      this.rowMaxX = new Float64Array(cap);
      this.cursor = new Int32Array(cap);
    }
    const rowStart = this.rowStart;
    const rowMinX = this.rowMinX;
    const rowMaxX = this.rowMaxX;
    rowStart.fill(0, 0, rows + 1);
    rowMinX.fill(Infinity, 0, rows);
    rowMaxX.fill(-Infinity, 0, rows);
    const c = set.coords;
    const r0 = set.polyStarts[poly];
    const r1 = set.polyStarts[poly + 1];
    const inv = 1 / s;
    // Pass 1: entries per row + x-extents. A sample in row gz has
    // y ∈ [gz·s, (gz+1)·s), so an edge spanning [ya, yb) can only matter to
    // rows floor(ya/s)-1 … floor(yb/s)+1 (a row of slack each side covers
    // division rounding; extra edges never change the parity).
    for (let r = r0; r < r1; r++) {
      const p0 = set.ringStarts[r];
      const p1 = set.ringStarts[r + 1];
      for (let i = p0, j = p1 - 1; i < p1; j = i++) {
        const yi = c[i * 2 + 1];
        const yj = c[j * 2 + 1];
        if (yi === yj) continue; // horizontal — never straddles a sample
        const ya = yi < yj ? yi : yj;
        const yb = yi < yj ? yj : yi;
        let ra = Math.floor(ya * inv) - 1 - gz0;
        let rb = Math.floor(yb * inv) + 1 - gz0;
        if (ra < 0) ra = 0;
        if (rb > rows - 1) rb = rows - 1;
        if (ra > rb) continue;
        const xi = c[i * 2];
        const xj = c[j * 2];
        const xa = xi < xj ? xi : xj;
        const xb = xi < xj ? xj : xi;
        for (let k = ra; k <= rb; k++) {
          rowStart[k + 1]++;
          if (xa < rowMinX[k]) rowMinX[k] = xa;
          if (xb > rowMaxX[k]) rowMaxX[k] = xb;
        }
      }
    }
    for (let k = 0; k < rows; k++) rowStart[k + 1] += rowStart[k];
    const total = rowStart[rows];
    if (this.ei.length < total) {
      const cap = Math.max(total, this.ei.length * 2);
      this.ei = new Int32Array(cap);
      this.ej = new Int32Array(cap);
    }
    const ei = this.ei;
    const ej = this.ej;
    const cursor = this.cursor;
    cursor.set(rowStart.subarray(0, rows));
    // Pass 2: fill (same walk, same row bounds).
    for (let r = r0; r < r1; r++) {
      const p0 = set.ringStarts[r];
      const p1 = set.ringStarts[r + 1];
      for (let i = p0, j = p1 - 1; i < p1; j = i++) {
        const yi = c[i * 2 + 1];
        const yj = c[j * 2 + 1];
        if (yi === yj) continue;
        const ya = yi < yj ? yi : yj;
        const yb = yi < yj ? yj : yi;
        let ra = Math.floor(ya * inv) - 1 - gz0;
        let rb = Math.floor(yb * inv) + 1 - gz0;
        if (ra < 0) ra = 0;
        if (rb > rows - 1) rb = rows - 1;
        for (let k = ra; k <= rb; k++) {
          const q = cursor[k]++;
          ei[q] = i;
          ej[q] = j;
        }
      }
    }
    return this;
  }

  /** Even-odd test against row `row`'s edges — identical to a full-ring test. */
  inside(x: number, y: number, row: number, c: Float64Array): boolean {
    let inside = false;
    const ei = this.ei;
    const ej = this.ej;
    const k1 = this.rowStart[row + 1];
    for (let k = this.rowStart[row]; k < k1; k++) {
      const i = ei[k];
      const j = ej[k];
      const xi = c[i * 2];
      const yi = c[i * 2 + 1];
      const xj = c[j * 2];
      const yj = c[j * 2 + 1];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }
}

const rowEdges = new RowEdgeIndex();

/**
 * Allocation-free hash set of lattice cells (gx, gz) — the per-tier "already
 * planted" dedup. A single numeric key wide enough for both coordinates falls
 * outside V8's small-integer range, so the old `Set<number>` boxed a heap
 * number on every probe: millions of allocations per rebuild on a large view.
 */
class CellSet {
  private kx: Int32Array;
  private kz: Int32Array;
  private used: Uint8Array;
  private mask: number;
  private size = 0;

  constructor(cap = 1 << 14) {
    this.kx = new Int32Array(cap);
    this.kz = new Int32Array(cap);
    this.used = new Uint8Array(cap);
    this.mask = cap - 1;
  }

  private static slot(x: number, z: number, mask: number): number {
    let h = Math.imul(x, 0x9e3779b1) ^ Math.imul(z, 0x85ebca77);
    h ^= h >>> 15;
    h = Math.imul(h, 0x2c1b3c6d);
    h ^= h >>> 12;
    return h & mask;
  }

  has(x: number, z: number): boolean {
    const used = this.used;
    const kx = this.kx;
    const kz = this.kz;
    const mask = this.mask;
    let h = CellSet.slot(x, z, mask);
    while (used[h]) {
      if (kx[h] === x && kz[h] === z) return true;
      h = (h + 1) & mask;
    }
    return false;
  }

  add(x: number, z: number) {
    if ((this.size + 1) * 2 > this.kx.length) this.grow();
    const used = this.used;
    const kx = this.kx;
    const kz = this.kz;
    const mask = this.mask;
    let h = CellSet.slot(x, z, mask);
    while (used[h]) {
      if (kx[h] === x && kz[h] === z) return;
      h = (h + 1) & mask;
    }
    used[h] = 1;
    kx[h] = x;
    kz[h] = z;
    this.size++;
  }

  private grow() {
    const oldX = this.kx;
    const oldZ = this.kz;
    const oldUsed = this.used;
    const cap = oldX.length * 2;
    this.kx = new Int32Array(cap);
    this.kz = new Int32Array(cap);
    this.used = new Uint8Array(cap);
    this.mask = cap - 1;
    this.size = 0;
    for (let i = 0; i < oldX.length; i++) {
      if (oldUsed[i]) this.add(oldX[i], oldZ[i]);
    }
  }
}

/** Shoelace area (mercator units²) of one member polygon (holes cancel). */
function polyAreaFlat(set: PolySet, poly: number): number {
  let area = 0;
  const r0 = set.polyStarts[poly];
  const r1 = set.polyStarts[poly + 1];
  const c = set.coords;
  for (let r = r0; r < r1; r++) {
    const p0 = set.ringStarts[r];
    const p1 = set.ringStarts[r + 1];
    let s = 0;
    for (let i = p0, j = p1 - 1; i < p1; j = i++) {
      s += c[j * 2] * c[i * 2 + 1] - c[i * 2] * c[j * 2 + 1];
    }
    area += s;
  }
  return Math.abs(area) / 2;
}

/** Fraction of bbox `b` overlapped by bbox `v` (0 when disjoint). */
function bboxOverlapRatio(b: Bbox | Float64Array, o: number, v: Bbox): number {
  const b0 = (b as Float64Array)[o];
  const b1 = (b as Float64Array)[o + 1];
  const b2 = (b as Float64Array)[o + 2];
  const b3 = (b as Float64Array)[o + 3];
  const ix = Math.min(b2, v[2]) - Math.max(b0, v[0]);
  const iy = Math.min(b3, v[3]) - Math.max(b1, v[1]);
  if (ix <= 0 || iy <= 0) return 0;
  const bw = Math.max(b2 - b0, 1e-12);
  const bh = Math.max(b3 - b1, 1e-12);
  return clamp((ix * iy) / (bw * bh), 0, 1);
}

/** Point inside a convex quad (8 numbers, consistent winding). */
function pointInQuad(x: number, y: number, q: Float64Array): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const ax = q[i * 2];
    const ay = q[i * 2 + 1];
    const j = (i + 1) & 3;
    const cr = (q[j * 2] - ax) * (y - ay) - (q[j * 2 + 1] - ay) * (x - ax);
    if (cr !== 0) {
      const s = cr > 0 ? 1 : -1;
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
  }
  return true;
}

/** Bilinear terrain elevation at a mercator point; NaN if a corner is missing. */
function elevAt(g: ElevGridData, mx: number, my: number): number {
  const fx = clamp((mx - g.x0) / g.dx, 0, g.n - 1.0001);
  const fy = clamp((my - g.y0) / g.dy, 0, g.n - 1.0001);
  const ix = Math.floor(fx);
  const iy = Math.floor(fy);
  const tx = fx - ix;
  const ty = fy - iy;
  const v = g.vals;
  const a = v[iy * g.n + ix];
  const b = v[iy * g.n + ix + 1];
  const c = v[(iy + 1) * g.n + ix];
  const d = v[(iy + 1) * g.n + ix + 1];
  const top = a + (b - a) * tx;
  const bot = c + (d - c) * tx;
  return top + (bot - top) * ty;
}

/** Nearest-cell fire sample index at a mercator point. */
function fireSampleIdx(g: FireGridData, mx: number, my: number): number {
  const ix = clamp(Math.round((mx - g.x0) / g.dx), 0, g.n - 1);
  const iy = clamp(Math.round((my - g.y0) / g.dy), 0, g.n - 1);
  return iy * g.n + ix;
}

// ---------------------------------------------------------------------------
// Instance buckets — growable flat arrays, matrices composed in place
// ---------------------------------------------------------------------------

// Shared per-plant orientation state (quaternion), set before push() calls.
let qx = 0;
let qy = 0;
let qz = 0;
let qw = 1;

/** Set the shared quaternion from XYZ euler angles (THREE order 'XYZ'). */
function setQuatFromEuler(ex: number, ey: number, ez: number) {
  const c1 = Math.cos(ex / 2);
  const c2 = Math.cos(ey / 2);
  const c3 = Math.cos(ez / 2);
  const s1 = Math.sin(ex / 2);
  const s2 = Math.sin(ey / 2);
  const s3 = Math.sin(ez / 2);
  qx = s1 * c2 * c3 + c1 * s2 * s3;
  qy = c1 * s2 * c3 - s1 * c2 * s3;
  qz = c1 * c2 * s3 + s1 * s2 * c3;
  qw = c1 * c2 * c3 - s1 * s2 * s3;
}

// Rotated crown-anchor offset (replaces THREE Vector3.applyQuaternion).
let crownX = 0;
let crownY = 0;
let crownZ = 0;
function crownAt(offY: number) {
  // q * (0, offY, 0) * q⁻¹
  const ix = qw * 0 + qy * 0 - qz * offY;
  const iy = qw * offY + qz * 0 - qx * 0;
  const iz = qw * 0 + qx * offY - qy * 0;
  const iw = -qx * 0 - qy * offY - qz * 0;
  crownX = ix * qw + iw * -qx + iy * -qz - iz * -qy;
  crownY = iy * qw + iw * -qy + iz * -qx - ix * -qz;
  crownZ = iz * qw + iw * -qz + ix * -qy - iy * -qx;
}

class Bucket {
  n = 0;
  private cap: number;
  mat: Float32Array;
  col: Float32Array;
  pr: Float32Array;
  fa: Float32Array | null;
  fr: Float32Array | null;

  constructor(initialCap: number, fire: boolean) {
    this.cap = initialCap;
    this.mat = new Float32Array(initialCap * 16);
    this.col = new Float32Array(initialCap * 3);
    this.pr = new Float32Array(initialCap);
    this.fa = fire ? new Float32Array(initialCap) : null;
    this.fr = fire ? new Float32Array(initialCap) : null;
  }

  private grow() {
    const cap = this.cap * 2;
    const mat = new Float32Array(cap * 16);
    mat.set(this.mat);
    this.mat = mat;
    const col = new Float32Array(cap * 3);
    col.set(this.col);
    this.col = col;
    const pr = new Float32Array(cap);
    pr.set(this.pr);
    this.pr = pr;
    if (this.fa) {
      const fa = new Float32Array(cap);
      fa.set(this.fa);
      this.fa = fa;
    }
    if (this.fr) {
      const fr = new Float32Array(cap);
      fr.set(this.fr);
      this.fr = fr;
    }
    this.cap = cap;
  }

  /** Compose position/shared-quat/scale straight into the matrix array. */
  push(
    px: number,
    py: number,
    pz: number,
    sx: number,
    sy: number,
    sz: number,
    c: ColorL,
    pr: number,
    fireArrival: number,
    fireReached: number
  ) {
    if (this.n >= this.cap) this.grow();
    const i = this.n++;
    const m = this.mat;
    const o = i * 16;
    const x2 = qx + qx;
    const y2 = qy + qy;
    const z2 = qz + qz;
    const xx = qx * x2;
    const xy = qx * y2;
    const xz = qx * z2;
    const yy = qy * y2;
    const yz = qy * z2;
    const zz = qz * z2;
    const wx = qw * x2;
    const wy = qw * y2;
    const wz = qw * z2;
    m[o] = (1 - (yy + zz)) * sx;
    m[o + 1] = (xy + wz) * sx;
    m[o + 2] = (xz - wy) * sx;
    m[o + 3] = 0;
    m[o + 4] = (xy - wz) * sy;
    m[o + 5] = (1 - (xx + zz)) * sy;
    m[o + 6] = (yz + wx) * sy;
    m[o + 7] = 0;
    m[o + 8] = (xz + wy) * sz;
    m[o + 9] = (yz - wx) * sz;
    m[o + 10] = (1 - (xx + yy)) * sz;
    m[o + 11] = 0;
    m[o + 12] = px;
    m[o + 13] = py;
    m[o + 14] = pz;
    m[o + 15] = 1;
    this.col[i * 3] = c.r;
    this.col[i * 3 + 1] = c.g;
    this.col[i * 3 + 2] = c.b;
    this.pr[i] = pr;
    if (this.fa) this.fa[i] = fireArrival;
    if (this.fr) this.fr[i] = fireReached;
  }

  /** Sort by priority (movement-LOD cut keeps whole trees) and trim. */
  finish(): BucketResult {
    const n = this.n;
    const idx = new Uint32Array(n);
    for (let i = 0; i < n; i++) idx[i] = i;
    const pr = this.pr;
    // Uint32Array.sort with a comparator sorts numerically without boxing.
    idx.sort((a, b) => pr[a] - pr[b]);
    const mat = new Float32Array(n * 16);
    const col = new Float32Array(n * 3);
    const fa = this.fa ? new Float32Array(n) : undefined;
    const fr = this.fr ? new Float32Array(n) : undefined;
    for (let i = 0; i < n; i++) {
      const s = idx[i];
      mat.set(this.mat.subarray(s * 16, s * 16 + 16), i * 16);
      col[i * 3] = this.col[s * 3];
      col[i * 3 + 1] = this.col[s * 3 + 1];
      col[i * 3 + 2] = this.col[s * 3 + 2];
      if (fa) fa[i] = this.fa![s];
      if (fr) fr[i] = this.fr![s];
    }
    return { count: n, matrices: mat, colors: col, fireArrival: fa, fireReached: fr };
  }
}

// ---------------------------------------------------------------------------
// The planting run
// ---------------------------------------------------------------------------

export function runPlanting(req: PlantRequest): PlantResult {
  const t0 = performance.now();
  const {
    view,
    quad,
    refX,
    refY,
    scale,
    zoom,
    terrainOn,
    elev,
    fire,
    polySets,
    camX,
    camY,
    camAltM,
    taperD0,
    farDistM,
    dropFarTrunks,
  } = req;

  // Coarser stand density when zoomed out, in discrete tiers so trees do not
  // reshuffle on sub-tier zoom changes. Below z13 the pitch doubles per zoom
  // level — tracking metres-per-pixel — so the per-view candidate count stays
  // bounded all the way out (the lattice snap quantizes this to √2 steps).
  const tierMul =
    zoom >= 15
      ? 1
      : zoom >= 14
      ? 1.6
      : 2.4 * Math.pow(2, Math.max(0, 13 - zoom));
  const sizeMul = req.sizeMul;
  /** Exaggerated (zoomed-out) trees: low-poly crowns planted near the ground
   *  — at ~10 px a high crown base reads as floating confetti, not a tree. */
  const exaggerated = sizeMul > 1.01;

  // 3D camera distance (m²) at a mercator point — altitude included, so both
  // the taper and the far-LOD split track true perspective size on screen.
  const invScale = 1 / scale;
  const altSq = camAltM * camAltM;
  const distSqM = (mx: number, my: number): number => {
    const dx = (mx - camX) * invScale;
    const dy = (my - camY) * invScale;
    return dx * dx + dy * dy + altSq;
  };
  const taperOn = taperD0 > 0;
  const d0Sq = taperD0 * taperD0;
  const farSq = farDistM * farDistM;
  /** Fraction of trees kept at a squared camera distance: 1 inside taperD0,
   *  then (d0/d)^1.5 — matching how screen area shrinks — floored at 0.1. */
  const keepAt = (d3Sq: number): number => {
    if (!taperOn || d3Sq <= d0Sq) return 1;
    const k = Math.pow(d0Sq / d3Sq, 0.75);
    return k < 0.1 ? 0.1 : k;
  };

  // Fraction of the view bbox the ground trapezoid actually covers. The
  // expected count must see the same culls the planting loop applies — an
  // estimate over the raw bbox at high pitch overshoots so far that the
  // uniform density backoff thins the whole lattice, near field included.
  let quadFactor = 1;
  if (quad) {
    let qa = 0;
    for (let i = 0, j = 3; i < 4; j = i++) {
      qa += quad[j * 2] * quad[i * 2 + 1] - quad[i * 2] * quad[j * 2 + 1];
    }
    qa = Math.abs(qa) / 2;
    const va = (view[2] - view[0]) * (view[3] - view[1]);
    if (va > 0) quadFactor = clamp(qa / va, 0.2, 1);
  }

  // ---- pass 1: expected plant count → uniform density backoff ------------
  let expected = 0;
  for (const stand of req.stands) {
    const spacingM = stand.spacingM * tierMul;
    const set = polySets[stand.polySet];
    const nPolys = set.polyStarts.length - 1;
    for (let p = 0; p < nPolys; p++) {
      const clipRatio = bboxOverlapRatio(set.bboxes, p * 4, view);
      if (clipRatio === 0) continue;
      const areaM2 = polyAreaFlat(set, p) / (scale * scale);
      // Approximate the taper over the polygon by its bbox-centre distance.
      const bo = p * 4;
      const keepEst = keepAt(
        distSqM(
          (Math.max(set.bboxes[bo], view[0]) + Math.min(set.bboxes[bo + 2], view[2])) / 2,
          (Math.max(set.bboxes[bo + 1], view[1]) + Math.min(set.bboxes[bo + 3], view[3])) / 2
        )
      );
      expected +=
        ((areaM2 * clipRatio) / (spacingM * spacingM)) * 0.9 * quadFactor * keepEst;
    }
  }
  let stepMul = 1;
  while (stepMul < 8 && expected / (stepMul * stepMul) > req.maxTrees * 0.85) {
    stepMul *= 1.5;
  }

  // ---- pass 2: plant trees ------------------------------------------------
  const trunks = new Bucket(4096, false);
  const conesNear = new Bucket(4096, true);
  const conesFar = new Bucket(4096, true);
  const blobsNear = new Bucket(4096, true);
  const blobsFar = new Bucket(4096, true);
  // Per-lattice-tier dedup of planted cells (typed hash set — no allocation).
  const planted = new Map<number, CellSet>();
  const color = new ColorL();
  const color2 = new ColorL();
  let total = 0;
  let capped = false;
  let terrainMissing = false;
  let bMinX = Infinity,
    bMaxX = -Infinity,
    bMinZ = Infinity,
    bMaxZ = -Infinity,
    bMinY = Infinity,
    bMaxY = -Infinity;

  for (const stand of req.stands) {
    if (capped) break;
    const set = polySets[stand.polySet];
    const nPolys = set.polyStarts.length - 1;
    // Snap the grid pitch to quantized √2-steps of the raw mercator unit so
    // the planting grid is a fixed global lattice (see original for why).
    const desiredMerc = stand.spacingM * tierMul * stepMul * scale;
    const tierExp = Math.round(Math.log2(desiredMerc) * 2);
    const spacingMerc = Math.pow(2, tierExp / 2);
    let tierSet = planted.get(tierExp);
    if (!tierSet) {
      tierSet = new CellSet();
      planted.set(tierExp, tierSet);
    }
    const gapThresh = stand.kind === "shrub" ? 0.92 : 0.9;
    const drawnBaseFrac = exaggerated
      ? Math.min(stand.baseFrac, 0.15)
      : Math.min(stand.baseFrac, 1 - MIN_CROWN_FRAC);

    for (let p = 0; p < nPolys && !capped; p++) {
      const bo = p * 4;
      const bx0 = Math.max(set.bboxes[bo], view[0]);
      const by0 = Math.max(set.bboxes[bo + 1], view[1]);
      const bx1 = Math.min(set.bboxes[bo + 2], view[2]);
      const by1 = Math.min(set.bboxes[bo + 3], view[3]);
      if (bx0 > bx1 || by0 > by1) continue;
      const gx0 = Math.floor(bx0 / spacingMerc);
      const gx1 = Math.ceil(bx1 / spacingMerc);
      const gz0 = Math.floor(by0 / spacingMerc);
      const gz1 = Math.ceil(by1 / spacingMerc);
      // Off-thread and mostly hash-reject-per-cell, so a horizon-reaching poly
      // is affordable to walk; the guard only drops truly pathological ones.
      if ((gx1 - gx0 + 1) * (gz1 - gz0 + 1) > 5000000) continue;
      // Index the polygon's edges by lattice row once: each cell then tests
      // only the edges crossing its row, and rows/cells the polygon cannot
      // reach are never walked at all (see RowEdgeIndex).
      const edges = rowEdges.build(set, p, spacingMerc, gz0, gz1);
      const rowStart = edges.rowStart;
      const rowMinX = edges.rowMinX;
      const rowMaxX = edges.rowMaxX;

      for (let gz = gz0; gz <= gz1 && !capped; gz++) {
        const row = gz - gz0;
        if (rowStart[row] === rowStart[row + 1]) continue; // no edges → outside
        const rxMin = rowMinX[row];
        const rxMax = rowMaxX[row];
        // A cell's sample lies in [gx·s, (gx+1)·s), so only cells overlapping
        // the row's x-extent can be inside (one cell of slack each side).
        const gxa = Math.max(gx0, Math.floor(rxMin / spacingMerc) - 1);
        const gxb = Math.min(gx1, Math.floor(rxMax / spacingMerc) + 1);
        for (let gx = gxa; gx <= gxb; gx++) {
          if (tierSet.has(gx, gz)) continue;
          if (hash2(gx, gz) > gapThresh) continue; // natural gaps
          const jx = hash2(gx * 3 + 1, gz);
          const jz = hash2(gx, gz * 5 + 2);
          const mx = (gx + 0.12 + 0.76 * jx) * spacingMerc;
          const my = (gz + 0.12 + 0.76 * jz) * spacingMerc;
          if (mx < rxMin || mx > rxMax) continue; // outside every row edge

          // Distance-tapered density: full pitch near the camera (3D distance,
          // so the foreground at high pitch is never thinned), hash-thinned
          // (stable per cell — a tree that exists keeps existing as you
          // approach) where trees shrink toward sub-pixel.
          const d3Sq = distSqM(mx, my);
          if (taperOn && d3Sq > d0Sq) {
            const keep = keepAt(d3Sq);
            if (hash2(gx + 171, gz + 173) > keep) continue;
          }
          // Cull to the projected view trapezoid, not just its bbox — at 60°
          // pitch the bbox covers ~2x the visible ground.
          if (quad && !pointInQuad(mx, my, quad)) continue;
          if (!edges.inside(mx, my, row, set.coords)) continue;

          const elevRaw = elev ? elevAt(elev, mx, my) : 0;
          if (Number.isNaN(elevRaw) && terrainOn) {
            // DEM tile not loaded yet — skip and retry once it arrives.
            terrainMissing = true;
            continue;
          }
          tierSet.add(gx, gz);

          if (++total > req.maxTrees) {
            capped = true;
            break;
          }

          const elevM = elevRaw ?? 0;
          const x = (mx - refX) / scale;
          const z = (my - refY) / scale;
          const far = exaggerated || d3Sq > farSq;
          const cones = far ? conesFar : conesNear;
          const blobs = far ? blobsFar : blobsNear;

          const treePr = hash2(gx + 7, gz + 13);
          const hJit = 0.9 + 0.2 * hash2(gx + 11, gz + 17);
          // Real height drives the fire physics; the drawn height carries the
          // zoomed-out exaggeration so trees hold a minimum screen size.
          const hReal = stand.height * hJit;
          const h = hReal * sizeMul;
          const baseH = drawnBaseFrac * h;
          const crownLen = Math.max(h - baseH, 0.35);

          // Fire state for this tree's cell (baked as per-instance attributes;
          // the shader animates from these — no per-tick CPU work).
          let fireArrival = FIRE_NEVER;
          let fireReached = 0;
          if (fire) {
            const fi = fireSampleIdx(fire, mx, my);
            const arr = fire.arrival[fi];
            if (Number.isFinite(arr)) {
              fireArrival = arr;
              fireReached = fire.flame[fi] >= CROWN_KILL_FRAC * hReal ? 1 : 0;
            }
          }
          if (x < bMinX) bMinX = x;
          if (x > bMaxX) bMaxX = x;
          if (z < bMinZ) bMinZ = z;
          if (z > bMaxZ) bMaxZ = z;
          if (elevM < bMinY) bMinY = elevM;
          if (elevM + h > bMaxY) bMaxY = elevM + h;

          const hHue = hash2(gx + 53, gz + 59);
          const hTone = hash2(gx + 41, gz + 43);
          const patchHue = (hash2(gx >> 4, gz >> 4) - 0.5) * 0.05;
          const hueShift = patchHue + (hHue - 0.5) * 0.06;

          // Slight lean + yaw shared by trunk and crown so they stay joined.
          const yaw = hash2(gx + 89, gz + 97) * Math.PI * 2;
          setQuatFromEuler(
            (hash2(gx + 79, gz + 83) - 0.5) * 0.09,
            yaw,
            (hash2(gx + 101, gz + 103) - 0.5) * 0.09
          );

          if (stand.kind === "shrub") {
            // One or two rounded woody lobes, no trunk.
            color.copy(SHRUB_LO).lerp(SHRUB_HI, stand.fullness);
            color.offsetHSL(hueShift, (hTone - 0.5) * 0.16, (hTone - 0.5) * 0.12);
            const lobes = hash2(gx + 5, gz + 7) < 0.5 ? 1 : 2;
            for (let b = 0; b < lobes; b++) {
              const a = hash2(gx * 5 + b + 2, gz + b * 3);
              const a2 = hash2(gx + b * 7, gz * 5 + b + 3);
              const r = h * 0.6 * (0.8 + 0.4 * hash2(gx + b, gz + b + 5));
              const sh = h * (0.8 + 0.3 * a2);
              blobs.push(
                x + (a - 0.5) * h * 0.6,
                elevM - 0.1,
                z + (a2 - 0.5) * h * 0.6,
                r * 2,
                sh,
                r * 2,
                color,
                treePr,
                fireArrival,
                fireReached
              );
            }
          } else if (stand.kind === "eucalypt") {
            // Pale straight bole + high, open blue-green crown (two airy lobes).
            if (!far || !dropFarTrunks) {
              // Realistic allometry: DBH ≈ 3.5% of height — a mature 40 m gum
              // carries a ~1.4 m bole, a 15 m regrowth stem ~0.5 m.
              const trunkR = Math.max(0.05, 0.0175 * h);
              color.copy(EUC_TRUNK);
              color.offsetHSL(
                (hHue - 0.5) * 0.03,
                (hTone - 0.5) * 0.06,
                (hTone - 0.5) * 0.12
              );
              trunks.push(
                x,
                elevM - 0.2,
                z,
                trunkR * 2,
                baseH + crownLen * 0.25,
                trunkR * 2,
                color,
                treePr,
                0,
                0
              );
            }
            color.copy(EUC_LO).lerp(EUC_HI, 0.35 + 0.5 * stand.fullness);
            color.offsetHSL(hueShift, (hTone - 0.5) * 0.16, (hTone - 0.5) * 0.12);
            const cr = Math.max(1.2, crownLen * 0.5 * (0.6 + 0.8 * stand.fullness));
            for (let b = 0; b < 2; b++) {
              const a = hash2(gx + b * 11 + 3, gz + b * 7);
              const a2 = hash2(gx + b * 13, gz + b * 17 + 3);
              crownAt(baseH + crownLen * (0.4 + 0.32 * b));
              const rr = cr * (b === 0 ? 1 : 0.72) * (0.85 + 0.3 * a);
              blobs.push(
                x + crownX + (a - 0.5) * cr * 0.5,
                elevM - 0.2 + crownY,
                z + crownZ + (a2 - 0.5) * cr * 0.5,
                rr * 2,
                crownLen * (0.5 - 0.12 * b),
                rr * 2,
                color,
                treePr,
                fireArrival,
                fireReached
              );
            }
            if (stand.understory > 0.4 && hash2(gx + 31, gz + 37) < 0.55) {
              const uh =
                Math.min(stand.understory, 2.5) * (0.7 + 0.5 * hTone) * sizeMul;
              const ur = uh * 0.7 + 0.3;
              color.copy(SHRUB_LO).lerp(SHRUB_HI, 0.5);
              color.offsetHSL(hueShift, 0, (hTone - 0.5) * 0.1);
              blobs.push(
                x + (hHue - 0.5) * 2,
                elevM - 0.1,
                z + (hTone - 0.5) * 2,
                ur * 2,
                uh,
                ur * 2,
                color,
                treePr,
                fireArrival,
                fireReached
              );
            }
          } else if (stand.kind === "pine") {
            // Red-brown bole + narrow dense dark-green spire (two stacked cones).
            if (!far || !dropFarTrunks) {
              // DBH ≈ 2.9% of height — a mature 26–30 m pine reads 0.75–0.9 m
              // through the trunk, a 10 m young stand ~0.3 m.
              const trunkR = Math.max(0.04, 0.0145 * h);
              color.copy(PINE_TRUNK);
              color.offsetHSL(
                (hHue - 0.5) * 0.03,
                (hTone - 0.5) * 0.08,
                (hTone - 0.5) * 0.12
              );
              trunks.push(
                x,
                elevM - 0.2,
                z,
                trunkR * 2,
                baseH + crownLen * 0.18,
                trunkR * 2,
                color,
                treePr,
                0,
                0
              );
            }
            color.copy(PINE_LO).lerp(PINE_HI, 0.4 + 0.6 * stand.fullness);
            color.offsetHSL(hueShift, (hTone - 0.5) * 0.12, (hTone - 0.5) * 0.1);
            // Plantation pine crowns stay narrow — ~4–6 m across at maturity
            // (the cap scales with the zoomed-out exaggeration).
            const cr = clamp(
              h * 0.09 * (0.7 + 0.6 * stand.fullness),
              0.7,
              3.0 * sizeMul
            );
            crownAt(baseH);
            cones.push(
              x + crownX,
              elevM - 0.2 + crownY,
              z + crownZ,
              cr * 2,
              crownLen * 0.7,
              cr * 2,
              color,
              treePr,
              fireArrival,
              fireReached
            );
            color2.copy(color);
            color2.offsetHSL(0.005, 0, 0.05);
            crownAt(baseH + crownLen * 0.42);
            cones.push(
              x + crownX,
              elevM - 0.2 + crownY,
              z + crownZ,
              cr * 1.2,
              crownLen * 0.58,
              cr * 1.2,
              color2,
              treePr,
              fireArrival,
              fireReached
            );
          } else {
            // Scott & Burgan forest — conifer / broadleaf decided per tree.
            const broadleaf = hash2(gx + 23, gz + 29) < 0.18;
            if (!far || !dropFarTrunks) {
              // DBH ≈ 3% of height (a 30 m conifer/broadleaf ~0.9 m).
              const trunkR = Math.max(0.05, 0.015 * h);
              color.copy(TRUNK);
              color.offsetHSL(
                (hHue - 0.5) * 0.05,
                (hTone - 0.5) * 0.1,
                (hTone - 0.5) * 0.14
              );
              trunks.push(
                x,
                elevM - 0.2,
                z,
                trunkR * 2,
                baseH + crownLen * (broadleaf ? 0.3 : 0.25),
                trunkR * 2,
                color,
                treePr,
                0,
                0
              );
            }
            const fullness = 0.55 + 0.9 * stand.fullness;
            color
              .copy(broadleaf ? BLOB_LO : CONE_LO)
              .lerp(broadleaf ? BLOB_HI : CONE_HI, stand.fullness);
            color.offsetHSL(hueShift, (hTone - 0.5) * 0.18, (hTone - 0.5) * 0.12);
            const rJit = 0.9 + 0.2 * hash2(gx + 61, gz + 67);
            if (broadleaf) {
              const cr = Math.max(1.0, crownLen * 0.45 * fullness) * rJit;
              const sx = cr * 2 * (0.82 + 0.36 * hash2(gx + 71, gz + 73));
              const sz = cr * 2 * (0.82 + 0.36 * hash2(gx + 107, gz + 109));
              crownAt(baseH);
              blobs.push(
                x + crownX,
                elevM - 0.2 + crownY,
                z + crownZ,
                sx,
                crownLen,
                sz,
                color,
                treePr,
                fireArrival,
                fireReached
              );
            } else {
              // Conifer spires stay slim: ~4–7 m across even on tall stems.
              const cr =
                clamp(crownLen * 0.22 * fullness, 0.8, 3.5 * sizeMul) * rJit;
              crownAt(baseH);
              cones.push(
                x + crownX,
                elevM - 0.2 + crownY,
                z + crownZ,
                cr * 2,
                crownLen * 0.7,
                cr * 2,
                color,
                treePr,
                fireArrival,
                fireReached
              );
              color2.copy(color);
              color2.offsetHSL(0.005, 0, 0.045);
              crownAt(baseH + crownLen * 0.42);
              cones.push(
                x + crownX,
                elevM - 0.2 + crownY,
                z + crownZ,
                cr * 1.24,
                crownLen * 0.58,
                cr * 1.24,
                color2,
                treePr,
                fireArrival,
                fireReached
              );
            }
          }
        }
      }
    }
  }

  // ---- pass 2.2: explicit crowns ------------------------------------------
  // The project's own trees and shrubs (Crown rows packed from the
  // fine-detail polygons): each has a centre, a crown radius and a height,
  // so it is drawn exactly where the imagery has it — radius used as-is, no
  // lattice, no hash gaps. The same view / terrain / far-LOD rules as the
  // lattice apply, and the taper thins them the same way in the distance.
  // Fire: a crown the urban graph ignited carries its own ignition time;
  // clump interiors (not in the graph) sample the fire grid like any tree.
  let crownCount = 0;
  const crowns = req.crowns;
  if (crowns && !capped) {
    const n = crowns.x.length;
    // Imagery-true sizes at ground level; only zoomed-out (sub-pixel) views
    // take the exaggeration so the crowns don't vanish with the forest.
    const crownSizeMul = zoom < 13 ? sizeMul : 1;
    for (let i = 0; i < n; i++) {
      const mx = crowns.x[i];
      const my = crowns.y[i];
      if (mx < view[0] || mx > view[2] || my < view[1] || my > view[3]) continue;
      const d3Sq = distSqM(mx, my);
      if (taperOn && d3Sq > d0Sq) {
        const keep = keepAt(d3Sq);
        if (hash2(i + 171, 173) > keep) continue;
      }
      if (quad && !pointInQuad(mx, my, quad)) continue;
      const elevRaw = elev ? elevAt(elev, mx, my) : 0;
      if (Number.isNaN(elevRaw) && terrainOn) {
        terrainMissing = true;
        continue;
      }
      if (++total > req.maxTrees) {
        capped = true;
        break;
      }
      crownCount++;

      const elevM = elevRaw ?? 0;
      const x = (mx - refX) / scale;
      const z = (my - refY) / scale;
      const far = exaggerated || d3Sq > farSq;
      const blobs = far ? blobsFar : blobsNear;
      const isShrub = crowns.kind[i] === 2;
      const hReal = crowns.heightM[i];
      const h = hReal * crownSizeMul;
      const r = crowns.radiusM[i] * crownSizeMul;
      const treePr = hash2(i + 7, 13);
      const hHue = hash2(i + 53, 59);
      const hTone = hash2(i + 41, 43);
      const hueShift = (hHue - 0.5) * 0.06;

      let fireArrival = FIRE_NEVER;
      let fireReached = 0;
      const own = crowns.fireArrival[i];
      if (own >= 0) {
        // The graph torched this crown at a known time.
        fireArrival = own;
        fireReached = 1;
      } else if (own === CROWN_FIRE_FROM_GRID && fire) {
        const fi = fireSampleIdx(fire, mx, my);
        const arr = fire.arrival[fi];
        if (Number.isFinite(arr)) {
          fireArrival = arr;
          fireReached = fire.flame[fi] >= CROWN_KILL_FRAC * hReal ? 1 : 0;
        }
      }
      if (x < bMinX) bMinX = x;
      if (x > bMaxX) bMaxX = x;
      if (z < bMinZ) bMinZ = z;
      if (z > bMaxZ) bMaxZ = z;
      if (elevM < bMinY) bMinY = elevM;
      if (elevM + h > bMaxY) bMaxY = elevM + h;

      const yaw = hash2(i + 89, 97) * Math.PI * 2;
      setQuatFromEuler((hash2(i + 79, 83) - 0.5) * 0.05, yaw, (hash2(i + 101, 103) - 0.5) * 0.05);

      if (isShrub) {
        // One or two rounded lobes filling the crown disc, no trunk.
        color.copy(SHRUB_LO).lerp(SHRUB_HI, 0.5);
        color.offsetHSL(hueShift, (hTone - 0.5) * 0.16, (hTone - 0.5) * 0.12);
        const lobes = hash2(i + 5, 7) < 0.5 ? 1 : 2;
        for (let b = 0; b < lobes; b++) {
          const a = hash2(i * 5 + b + 2, b * 3);
          const a2 = hash2(i + b * 7, 5 + b + 3);
          const rr = lobes === 1 ? r : r * 0.7;
          const off = lobes === 1 ? 0 : r * 0.35;
          blobs.push(
            x + (a - 0.5) * 2 * off,
            elevM - 0.1,
            z + (a2 - 0.5) * 2 * off,
            rr * 2,
            h * (0.85 + 0.3 * a2),
            rr * 2,
            color,
            treePr,
            fireArrival,
            fireReached
          );
        }
      } else {
        // A tree: straight bole to a crown base at 40% of height (the torching
        // threshold the fire graph uses), two lobes spanning the crown disc.
        const baseFrac = exaggerated ? 0.15 : 0.4;
        const baseH = baseFrac * h;
        const crownLen = Math.max(h - baseH, 0.35);
        if (!far || !dropFarTrunks) {
          const trunkR = Math.max(0.05, 0.0175 * h);
          color.copy(EUC_TRUNK);
          color.offsetHSL((hHue - 0.5) * 0.03, (hTone - 0.5) * 0.06, (hTone - 0.5) * 0.12);
          trunks.push(x, elevM - 0.2, z, trunkR * 2, baseH + crownLen * 0.25, trunkR * 2, color, treePr, 0, 0);
        }
        color.copy(EUC_LO).lerp(EUC_HI, 0.55);
        color.offsetHSL(hueShift, (hTone - 0.5) * 0.16, (hTone - 0.5) * 0.12);
        for (let b = 0; b < 2; b++) {
          const a = hash2(i + b * 11 + 3, b * 7);
          const a2 = hash2(i + b * 13, b * 17 + 3);
          crownAt(baseH + crownLen * (0.4 + 0.32 * b));
          const rr = r * (b === 0 ? 1 : 0.72);
          blobs.push(
            x + crownX + (a - 0.5) * r * 0.3,
            elevM - 0.2 + crownY,
            z + crownZ + (a2 - 0.5) * r * 0.3,
            rr * 2,
            crownLen * (0.55 - 0.12 * b),
            rr * 2,
            color,
            treePr,
            fireArrival,
            fireReached
          );
        }
      }
    }
  }

  // ---- pass 2.5: ground cover (litter, shrubs, logs, grass) ---------------
  const litter = new Bucket(2048, false);
  const logs = new Bucket(512, false);
  const nsShrubs = new Bucket(2048, false);
  const grass = new Bucket(4096, false);
  let gTotal = 0;

  if (
    elev &&
    zoom >= GROUND_MIN_ZOOM &&
    req.wantGround &&
    (req.showSurface || req.showGrass)
  ) {
    let gExpected = 0;
    for (const stand of req.groundStands) {
      const g = stand.ground;
      const presence =
        (req.showSurface ? g.litterProb + g.shrubProb + g.logProb : 0) +
        (req.showGrass ? g.grassProb : 0);
      if (presence <= 0) continue;
      const set = polySets[stand.polySet];
      const nPolys = set.polyStarts.length - 1;
      for (let p = 0; p < nPolys; p++) {
        const clipRatio = bboxOverlapRatio(set.bboxes, p * 4, view);
        if (clipRatio === 0) continue;
        const areaM2 = polyAreaFlat(set, p) / (scale * scale);
        gExpected += ((areaM2 * clipRatio) / (g.spacingM * g.spacingM)) * presence;
      }
    }
    let gStep = 1;
    while (gStep < 8 && gExpected / (gStep * gStep) > req.maxGround * 0.85) {
      gStep *= 1.5;
    }

    const gPlanted = new Map<number, CellSet>();
    let gCapped = false;

    for (const stand of req.groundStands) {
      if (gCapped) break;
      const g = stand.ground;
      const set = polySets[stand.polySet];
      const nPolys = set.polyStarts.length - 1;
      const desiredMerc = g.spacingM * gStep * scale;
      const tierExp = Math.round(Math.log2(desiredMerc) * 2);
      const spacingMerc = Math.pow(2, tierExp / 2);
      let tierSet = gPlanted.get(tierExp);
      if (!tierSet) {
        tierSet = new CellSet();
        gPlanted.set(tierExp, tierSet);
      }

      for (let p = 0; p < nPolys && !gCapped; p++) {
        const bo = p * 4;
        const bx0 = Math.max(set.bboxes[bo], view[0]);
        const by0 = Math.max(set.bboxes[bo + 1], view[1]);
        const bx1 = Math.min(set.bboxes[bo + 2], view[2]);
        const by1 = Math.min(set.bboxes[bo + 3], view[3]);
        if (bx0 > bx1 || by0 > by1) continue;
        const gx0 = Math.floor(bx0 / spacingMerc);
        const gx1 = Math.ceil(bx1 / spacingMerc);
        const gz0 = Math.floor(by0 / spacingMerc);
        const gz1 = Math.ceil(by1 / spacingMerc);
        if ((gx1 - gx0 + 1) * (gz1 - gz0 + 1) > 1500000) continue;
        const edges = rowEdges.build(set, p, spacingMerc, gz0, gz1);
        const rowStart = edges.rowStart;
        const rowMinX = edges.rowMinX;
        const rowMaxX = edges.rowMaxX;

        for (let gz = gz0; gz <= gz1 && !gCapped; gz++) {
          const row = gz - gz0;
          if (rowStart[row] === rowStart[row + 1]) continue;
          const rxMin = rowMinX[row];
          const rxMax = rowMaxX[row];
          const gxa = Math.max(gx0, Math.floor(rxMin / spacingMerc) - 1);
          const gxb = Math.min(gx1, Math.floor(rxMax / spacingMerc) + 1);
          for (let gx = gxa; gx <= gxb; gx++) {
            if (tierSet.has(gx, gz)) continue;
            const jx = hash2(gx * 3 + 1, gz);
            const jz = hash2(gx, gz * 5 + 2);
            const mx = (gx + 0.1 + 0.8 * jx) * spacingMerc;
            const my = (gz + 0.1 + 0.8 * jz) * spacingMerc;
            if (mx < rxMin || mx > rxMax) continue;
            if (quad && !pointInQuad(mx, my, quad)) continue;
            if (!edges.inside(mx, my, row, set.coords)) continue;

            const elevRaw = elevAt(elev, mx, my);
            if (Number.isNaN(elevRaw) && terrainOn) {
              terrainMissing = true;
              continue;
            }
            tierSet.add(gx, gz);
            const elevM = elevRaw ?? 0;
            const x = (mx - refX) / scale;
            const z = (my - refY) / scale;
            const pr = hash2(gx + 3, gz + 9);

            // Litter — a flat patch lying on the ground.
            if (req.showSurface && hash2(gx + 21, gz + 27) < g.litterProb) {
              const sf = g.litterFullness;
              const size = 0.5 + 1.3 * sf * (0.7 + 0.6 * hash2(gx + 5, gz + 8));
              color
                .copy(LITTER_LO)
                .lerp(
                  LITTER_HI,
                  clamp(sf * 0.7 + (hash2(gx + 13, gz + 15) - 0.5) * 0.4, 0, 1)
                )
                .offsetHSL(
                  (hash2(gx + 2, gz + 4) - 0.5) * 0.03,
                  (hash2(gx + 6, gz + 1) - 0.5) * 0.12,
                  (hash2(gx + 9, gz + 3) - 0.5) * 0.16
                );
              setQuatFromEuler(0, hash2(gx + 31, gz + 37) * Math.PI, 0);
              litter.push(
                x,
                elevM + 0.04,
                z,
                size * (0.8 + 0.5 * hash2(gx + 1, gz + 2)),
                1,
                size * (0.8 + 0.5 * hash2(gx + 7, gz + 5)),
                color,
                pr,
                0,
                0
              );
              if (++gTotal > req.maxGround) {
                gCapped = true;
                break;
              }
            }

            // Near-surface shrub — a low woody lobe under an elevated load.
            if (
              req.showSurface &&
              g.shrubProb > 0 &&
              hash2(gx + 71, gz + 73) < g.shrubProb
            ) {
              const hh = g.shrubHeight * (0.85 + 0.7 * hash2(gx + 81, gz + 83));
              const rr = hh * (0.55 + 0.4 * hash2(gx + 91, gz + 93)) + 0.25;
              color
                .copy(NS_SHRUB_LO)
                .lerp(NS_SHRUB_HI, clamp(0.3 + 0.6 * g.shrubFullness, 0, 1))
                .offsetHSL(
                  (hash2(gx + 2, gz + 9) - 0.5) * 0.04,
                  (hash2(gx + 4, gz + 7) - 0.5) * 0.16,
                  (hash2(gx + 8, gz + 6) - 0.5) * 0.14
                );
              setQuatFromEuler(0, hash2(gx + 95, gz + 97) * Math.PI * 2, 0);
              nsShrubs.push(x, elevM - 0.05, z, rr * 2, hh, rr * 2, color, pr, 0, 0);
              if (++gTotal > req.maxGround) {
                gCapped = true;
                break;
              }
            }

            // Fallen log — rare, only under heavy-surface-fuel tree stands.
            if (
              req.showSurface &&
              g.logProb > 0 &&
              hash2(gx + 41, gz + 47) < g.logProb
            ) {
              const big = hash2(gx + 12, gz + 19) ** 2;
              const len = 1.0 + 11.5 * big;
              const rad = 0.06 + 0.34 * big + 0.05 * hash2(gx + 22, gz + 8);
              color
                .copy(LOG_LO)
                .lerp(LOG_HI, hash2(gx + 3, gz + 33))
                .offsetHSL(
                  (hash2(gx + 27, gz + 4) - 0.5) * 0.03,
                  (hash2(gx + 6, gz + 31) - 0.5) * 0.14,
                  (hash2(gx + 18, gz + 9) - 0.5) * 0.14
                );
              setQuatFromEuler(
                (hash2(gx + 61, gz + 9) - 0.5) * 0.14,
                hash2(gx + 51, gz + 57) * Math.PI * 2,
                0
              );
              logs.push(x, elevM + rad, z, len, rad * 2, rad * 2, color, pr, 0, 0);
              if (++gTotal > req.maxGround) {
                gCapped = true;
                break;
              }
            }

            // Grass — dense in grassland, a grassy understory in open forest.
            if (
              req.showGrass &&
              g.grassProb > 0 &&
              hash2(gx + 101, gz + 103) < g.grassProb
            ) {
              const cellM = spacingMerc / scale;
              const clumps = Math.max(
                1,
                Math.round(g.grassClumps * req.grassClumpScale)
              );
              for (let c = 0; c < clumps; c++) {
                const ox = (hash2(gx * 7 + c + 1, gz + c * 3) - 0.5) * cellM * 0.9;
                const oz = (hash2(gx + c * 5, gz * 7 + c + 2) - 0.5) * cellM * 0.9;
                const hh =
                  g.grassHeight * (0.7 + 0.5 * hash2(gx + 111 + c, gz + 113));
                const ww = hh * (0.32 + 0.3 * hash2(gx + 121, gz + 123 + c)) + 0.08;
                color
                  .copy(GRASS_LO)
                  .lerp(
                    GRASS_HI,
                    clamp(
                      g.grassFullness * 0.6 +
                        (hash2(gx + 131 + c, gz + 133) - 0.3) * 0.7,
                      0,
                      1
                    )
                  )
                  .offsetHSL(
                    (hash2(gx + 12, gz + 19 + c) - 0.5) * 0.04,
                    (hash2(gx + 14 + c, gz + 17) - 0.5) * 0.18,
                    (hash2(gx + 18, gz + 16 + c) - 0.5) * 0.14
                  );
                setQuatFromEuler(0, hash2(gx + 141 + c, gz + 143) * Math.PI * 2, 0);
                grass.push(x + ox, elevM - 0.02, z + oz, ww, hh, ww, color, pr, 0, 0);
                if (++gTotal > req.maxGround) {
                  gCapped = true;
                  break;
                }
              }
              if (gCapped) break;
            }
          }
        }
      }
    }
  }

  return {
    gen: req.gen,
    terrainMissing,
    bounds: [bMinX, bMaxX, bMinZ, bMaxZ, bMinY, bMaxY],
    trunks: trunks.finish(),
    conesNear: conesNear.finish(),
    conesFar: conesFar.finish(),
    blobsNear: blobsNear.finish(),
    blobsFar: blobsFar.finish(),
    litter: litter.finish(),
    logs: logs.finish(),
    nsShrubs: nsShrubs.finish(),
    grass: grass.finish(),
    treeCount: total,
    crownCount,
    groundCount: gTotal,
    capped,
    plantMs: performance.now() - t0,
  };
}

/** Transferable buffers of a result (zero-copy postMessage). */
export function resultTransferables(r: PlantResult): ArrayBuffer[] {
  const out: ArrayBuffer[] = [];
  const add = (b: BucketResult) => {
    out.push(b.matrices.buffer as ArrayBuffer, b.colors.buffer as ArrayBuffer);
    if (b.fireArrival) out.push(b.fireArrival.buffer as ArrayBuffer);
    if (b.fireReached) out.push(b.fireReached.buffer as ArrayBuffer);
  };
  add(r.trunks);
  add(r.conesNear);
  add(r.conesFar);
  add(r.blobsNear);
  add(r.blobsFar);
  add(r.litter);
  add(r.logs);
  add(r.nsShrubs);
  add(r.grass);
  return out;
}
